import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '@/hooks/redux';
import {
  getVapidPublicKey,
  registerPushSubscription,
  removePushSubscription,
  getPushSubscriptions,
  PushSubscriptionInfo,
} from '@/services/notificationService';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { getDeviceName } from '@/utils/deviceName';

interface UsePushNotificationsResult {
  isSupported: boolean;
  isEnabled: boolean;
  isSubscribed: boolean;
  permission: NotificationPermission;
  subscriptions: PushSubscriptionInfo[];
  loading: boolean;
  error: string | null;
  subscribe: () => Promise<boolean>;
  unsubscribe: (subscriptionId?: string) => Promise<void>;
  refreshSubscriptions: () => Promise<void>;
}

/**
 * Converts a base64 URL-safe string to a Uint8Array for VAPID key
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Hook for managing Web Push Notifications
 */
export const usePushNotifications = (): UsePushNotificationsResult => {
  const { user } = useAppSelector((state) => state.auth);
  const { isOnline } = useOnlineStatus();
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscriptions, setSubscriptions] = useState<PushSubscriptionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      // Check for service worker and PushManager support
      const supported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

      setIsSupported(supported);

      if (supported) {
        setPermission(Notification.permission);
      }
    };

    checkSupport();
  }, []);

  // Check server configuration and current subscription status
  useEffect(() => {
    const initialize = async () => {
      if (!isSupported || !user?.parentId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Check if push is enabled on the server
        const vapidConfig = await getVapidPublicKey();
        setIsEnabled(vapidConfig.enabled);

        if (!vapidConfig.enabled) {
          setLoading(false);
          return;
        }

        // Fetch registered subscriptions from server
        const serverSubscriptions = await getPushSubscriptions();
        setSubscriptions(serverSubscriptions);

        // Check if this device's push endpoint is registered on the server
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          if (existingSubscription) {
            const endpoint = existingSubscription.endpoint;
            setIsSubscribed(serverSubscriptions.some(sub => sub.endpoint === endpoint));
          } else {
            setIsSubscribed(false);
          }
        }
      } catch (err) {
        console.error('[PushNotifications] Initialization error:', err);
        setError('Failed to initialize push notifications');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [isSupported, user?.parentId, isOnline]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !isEnabled) {
      setError('Push notifications are not supported or enabled');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request notification permission if not already granted
      if (permission !== 'granted') {
        const result = await Notification.requestPermission();
        setPermission(result);

        if (result !== 'granted') {
          setError('Notification permission was denied');
          return false;
        }
      }

      // Get VAPID public key
      const vapidConfig = await getVapidPublicKey();
      if (!vapidConfig.publicKey) {
        setError('Push notifications are not configured on the server');
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidConfig.publicKey) as BufferSource,
      });

      // Register subscription with server
      const deviceName = await getDeviceName();
      const subscriptionJson = subscription.toJSON();

      if (!subscriptionJson.endpoint || !subscriptionJson.keys) {
        throw new Error('Invalid subscription data');
      }

      await registerPushSubscription(subscriptionJson, deviceName);

      setIsSubscribed(true);

      // Refresh subscriptions list
      const serverSubscriptions = await getPushSubscriptions();
      setSubscriptions(serverSubscriptions);

      return true;
    } catch (err) {
      console.error('[PushNotifications] Subscribe error:', err);
      setError(err instanceof Error ? err.message : 'Failed to subscribe to push notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, isEnabled, permission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(
    async (subscriptionId?: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        if (subscriptionId) {
          // Remove specific subscription from server
          await removePushSubscription(subscriptionId);
        } else {
          // Disable All: remove all server-side subscriptions
          const currentSubs = await getPushSubscriptions();
          await Promise.all(currentSubs.map((sub) => removePushSubscription(sub.id)));

          // Unsubscribe from local push manager
          const registrations = await navigator.serviceWorker.getRegistrations();
          if (registrations.length > 0) {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
              await subscription.unsubscribe();
            }
          }
        }

        // Refresh subscriptions list
        const serverSubscriptions = await getPushSubscriptions();
        setSubscriptions(serverSubscriptions);

        // Check if this device's endpoint is still registered on the server
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
          const endpoint = existingSubscription.endpoint;
          setIsSubscribed(serverSubscriptions.some(sub => sub.endpoint === endpoint));
        } else {
          setIsSubscribed(false);
        }
      } catch (err) {
        console.error('[PushNotifications] Unsubscribe error:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to unsubscribe from push notifications'
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Refresh subscriptions list
  const refreshSubscriptions = useCallback(async (): Promise<void> => {
    if (!isEnabled) return;

    try {
      const serverSubscriptions = await getPushSubscriptions();
      setSubscriptions(serverSubscriptions);
    } catch (err) {
      console.error('[PushNotifications] Failed to refresh subscriptions:', err);
    }
  }, [isEnabled]);

  return {
    isSupported,
    isEnabled,
    isSubscribed,
    permission,
    subscriptions,
    loading,
    error,
    subscribe,
    unsubscribe,
    refreshSubscriptions,
  };
};
